// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { flattenDateMathCalculations } from 'datastage/utils/export/flattenDateMathCalculations';
// --- Hooks & Store
import { useDateMathCalculationsActions } from 'datastage/store/dateMathCalculations/useDateMathCalculations';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import './styles.scss';

// Fetch date math calculations based on filter criteria
export const fetchDateMathCalculations = async (filter = {}, rangeParam, sortParam) => {
    try {
        return await datastageApi.dateMathCalculations(filter, rangeParam, sortParam);
    } catch (error) {
        console.error('Error fetching date math calculations:', error);
        return null;
    }
};

/**
 * Date Math Calculations Search Form
 *
 * Features:
 * - Renders a filter form for Date Math Calculations (Job ID, Load Status)
 * - Handles filter, reset, export, and refresh actions
 * - Triggers data fetch and updates Redux state
 *
 * @returns {Element}
 * @constructor
 */
export function DateMathCalculationsSearchForm() {

    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const {showErrorToast } = useToasts();
    const { updateDateMathCalculations, updateDateMathCalculationsSearchForm, clearDateMathCalculations } = useDateMathCalculationsActions();
    const {
        filterForm,
        rangeParam,
        sortParam,
        loadStatusOptions,
        selectedJob,
        totalItems,
    } = useSelector((s) => s.dateMathCalculations);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [statuses] = await Promise.all([
                    datastageApi.jobStatuses(true),
                ]);
                updateDateMathCalculations({
                    loadStatusOptions: statuses,
                });
            } catch (error) {
                console.error('Error fetching job statuses:', error);
            }
        };
        fetchOptions();
        setTimeout(handleFilter, 5);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Fetch date math calculations based on filter criteria
    const handleFilter = async () => {
        setLoadingTrue();
        try {
            const data = await fetchDateMathCalculations(
                filterCriteria(filterForm, '', 'dateMathCalculations'),
                rangeParam,
                sortParam,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch date math calculations data.');
                return;
            }
            updateDateMathCalculations({
                calculations: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            showErrorToast(error?.message || 'An error occurred while fetching date math calculations data.');
            console.error('Error filtering date math calculations:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle reset filter action
    const handleResetFilter = async () => {
        setLoadingTrue();
        try {

            updateDateMathCalculationsSearchForm({
                key: 'filterForm',
                data: { jobId: selectedJob?.id, loadStatus: '' },
            })

            const resetFilterForm = { jobId: selectedJob?.id };

            const data = await fetchDateMathCalculations(resetFilterForm, rangeParam, sortParam);

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch date math calculations data.');
                return;
            }


            updateDateMathCalculations({
                calculations: data?.content,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            showErrorToast(error?.message || 'An error occurred while fetching date math calculations data.');
            console.error('Error resetting filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Export current calculations to CSV
    const handleExport = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.dateMathCalculations(
                filterCriteria(filterForm, '', 'dateMathCalculations'),
                '0,999',
                sortParam,
            );
            if (data?.code) {
                showErrorToast(data?.message || 'Failed to export date math calculations data.');
                return;
            }

            const filteredContent = flattenDateMathCalculations(data?.content);
            exportToCsv(filteredContent, 'datemath_calculations');

        } catch (error) {
            showErrorToast(error?.message || 'An error occurred while exporting date math calculations data.');
            console.error('Error exporting date math calculations:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Refresh the calculations list
    const handleRefresh = () => {
        handleFilter();
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateDateMathCalculationsSearchForm({
            key: 'filterForm',
            data: { [name]: value },
        });
    };

    const handleSelectChange = (name, value) => {
        updateDateMathCalculationsSearchForm({
            key: 'filterForm',
            data: { [name]: value },
        });
    };

    return (
      <Row className="calculations-filter-form" align="middle">
            <Col>
                <InputLabel colProps={{ span: 24 }} label='Date Math Job ID'>
                    <Input
                        value={filterForm.jobId}
                        onChange={handleInputChange}
                        width="auto"
                        id='jobId'
                        name='jobId'
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Load Status">
                    <Select
                        options={loadStatusOptions}
                        isClearable
                        isSearchable
                        onChange={value => handleSelectChange('loadStatus', value)}
                        value={filterForm.loadStatus}
                    />
                </InputLabel>
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 10, marginTop: 15 }}>
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
            <Col flex="auto" />
            <Col style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 15, justifyContent: 'flex-end' }}>
                <Button
                    iconType="fa-solid fa-arrow-down-to-line"
                    labelText="Export"
                    styleType="link"
                    onClick={handleExport}
                    disabled={totalItems === 0}
                >
                    Export
                </Button>
                <Button
                    iconType="fa-solid fa-rotate"
                    labelText="Refresh"
                    styleType="link"
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </Col>
        </Row>
    );
}
